/**
 * Keyboard Shortcuts for CAD System
 * Provides keyboard-based workflow acceleration
 */

class CADKeyboardShortcuts {
  constructor(cadViewer) {
    this.cadViewer = cadViewer;
    this.enabled = true;
    this.initialize();
  }

  initialize() {
    document.addEventListener('keydown', (e) => this.handleKeyDown(e));
    console.log('[CADKeyboardShortcuts] Initialized');
  }

  handleKeyDown(e) {
    if (!this.enabled) return;

    // Ignore if typing in input/textarea
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
      return;
    }

    const ctrl = e.ctrlKey || e.metaKey;
    const shift = e.shiftKey;
    const alt = e.altKey;

    // Undo/Redo
    if (ctrl && !shift && e.key === 'z') {
      e.preventDefault();
      if (this.cadViewer.historyManager) {
        this.cadViewer.historyManager.undo();
      }
      return;
    }

    if (ctrl && (e.key === 'y' || (shift && e.key === 'z'))) {
      e.preventDefault();
      if (this.cadViewer.historyManager) {
        this.cadViewer.historyManager.redo();
      }
      return;
    }

    // Save
    if (ctrl && e.key === 's') {
      e.preventDefault();
      this.cadViewer.saveDocument();
      return;
    }

    // Delete
    if (e.key === 'Delete' || e.key === 'Backspace') {
      if (this.cadViewer.selectedFeatureId) {
        e.preventDefault();
        this.cadViewer.deleteFeature(this.cadViewer.selectedFeatureId);
      }
      return;
    }

    // Sketch tools (when in sketch mode)
    if (this.cadViewer.sketchEngine?.activeSketch) {
      switch (e.key.toLowerCase()) {
        case 'l':
          e.preventDefault();
          this.cadViewer.activateSketchTool('line');
          break;
        case 'r':
          e.preventDefault();
          this.cadViewer.activateSketchTool('rectangle');
          break;
        case 'c':
          e.preventDefault();
          this.cadViewer.activateSketchTool('circle');
          break;
        case 'a':
          e.preventDefault();
          this.cadViewer.activateSketchTool('arc');
          break;
        case 'p':
          e.preventDefault();
          this.cadViewer.activateSketchTool('point');
          break;
        case 's':
          if (!ctrl) {
            e.preventDefault();
            this.cadViewer.activateSketchTool('spline');
          }
          break;
        case 't':
          e.preventDefault();
          this.cadViewer.activateSketchTool('text');
          break;
        case 'e':
          e.preventDefault();
          this.cadViewer.activateSketchTool('ellipse');
          break;
      }
    }

    // View shortcuts
    switch (e.key.toLowerCase()) {
      case '1':
        if (ctrl) {
          e.preventDefault();
          this.setView('front');
        }
        break;
      case '2':
        if (ctrl) {
          e.preventDefault();
          this.setView('back');
        }
        break;
      case '3':
        if (ctrl) {
          e.preventDefault();
          this.setView('top');
        }
        break;
      case '4':
        if (ctrl) {
          e.preventDefault();
          this.setView('bottom');
        }
        break;
      case '5':
        if (ctrl) {
          e.preventDefault();
          this.setView('left');
        }
        break;
      case '6':
        if (ctrl) {
          e.preventDefault();
          this.setView('right');
        }
        break;
      case '7':
        if (ctrl) {
          e.preventDefault();
          this.setView('isometric');
        }
        break;
    }

    // Quick operations
    if (e.key === 'Escape') {
      if (this.cadViewer.sketchEngine?.activeSketch) {
        this.cadViewer.sketchEngine.cancelSketch();
      }
    }

    if (e.key === 'Enter') {
      if (this.cadViewer.sketchEngine?.activeSketch) {
        this.cadViewer.sketchEngine.finishSketch();
      }
    }
  }

  setView(viewName) {
    const camera = this.cadViewer.camera;
    const controls = this.cadViewer.controls;
    const distance = 10;

    switch (viewName) {
      case 'front':
        camera.position.set(0, -distance, 0);
        break;
      case 'back':
        camera.position.set(0, distance, 0);
        break;
      case 'top':
        camera.position.set(0, 0, distance);
        break;
      case 'bottom':
        camera.position.set(0, 0, -distance);
        break;
      case 'left':
        camera.position.set(-distance, 0, 0);
        break;
      case 'right':
        camera.position.set(distance, 0, 0);
        break;
      case 'isometric':
        camera.position.set(distance, -distance, distance);
        break;
    }

    camera.lookAt(0, 0, 0);
    if (controls) controls.update();

    Utils.notify(`View: ${viewName}`, 'info');
  }

  enable() {
    this.enabled = true;
  }

  disable() {
    this.enabled = false;
  }
}

// Export
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { CADKeyboardShortcuts };
}
